/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Input, Modal, Tag, Typography, message } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import {
  DeleteOutlined,
  DownloadOutlined,
  PaperClipOutlined,
} from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { queryCache, useMutation, useQuery } from 'react-query';
import {
  deleteEvaluationCurrentInstantMessageAttachment,
  getEvaluationCurrentInstantMessage,
  getEvaluationCurrentInstantMessageAttachmentLocation,
  getEvaluationInstantMessageReceivers,
  sendEvaluationCurrentInstantMessage,
  updateEvaluationCurrentInstantMessage,
} from '~src/services/api/offers';
import {
  getGlobalEvaluationInstantMessage,
  getGlobalEvaluationInstantMessageAttachmentLocation,
  getGlobalEvaluationInstantMessageAttachmentLocationVendor,
  getGlobalEvaluationInstantMessageVendor,
} from '~src/services/api/movements';
import FileProgressContext from '~src/contexts/FileProgressContext';
import { v4 as uuid } from 'uuid';
import { SimpleConfirmModal } from '~components/popups-modals/ConfirmModal/SimpleConfirmModal';
import Viewer from '~components/atoms/Viewers/Viewer';
import IconButton from '~components/_common/IconButton/IconButton';

const MAX_ATTACHMENTS = 5;
const MAX_ATTACHMENT_SIZE_BYTES = 4 * 1024 * 1024; // 4 MB

type LocalAttachment = {
  id: string;
  name: string;
  size: number;
  fileGuid?: string;
};

type MessageRecipient = {
  id: string;
  name: string;
  email?: string;
  company?: string;
  role?: string;
  isPrimary?: boolean;
};

type OffersMessagesDraft = {
  recipients: MessageRecipient[];
  title: string;
  message: string;
  attachments: LocalAttachment[];
};

export type MessageDraftTabProps = {
  projectId: number;
  tenderId: number;
  contractId: number;
  contract?: any;
  visible: boolean;
  readOnly: boolean;
  shareId?: string;
  shareKey?: string;
  readOnlyData?: any;

  // Callbacks to parent
  onDirtyChange: (isDirty: boolean) => void;
  onCanSendChange: (canSend: boolean) => void;
  onSendSuccess?: () => void;
  onSendingChange: (isSending: boolean) => void;

  // Control from parent
  resetNonce?: number;
};

const createInitialDraft = (contract?: any): OffersMessagesDraft => ({
  recipients: [],
  title: '',
  message: '',
  attachments: [],
});

const cloneDraft = (draft: OffersMessagesDraft): OffersMessagesDraft => ({
  recipients: draft.recipients.map((r) => ({ ...r })),
  title: draft.title,
  message: draft.message,
  attachments: draft.attachments.map((a) => ({ ...a })),
});

export const MessageDraftTab = forwardRef<any, MessageDraftTabProps>(
  (
    {
      projectId,
      tenderId,
      contractId,
      contract,
      visible,
      readOnly,
      shareId,
      shareKey,
      readOnlyData,
      onDirtyChange,
      onCanSendChange,
      onSendSuccess,
      onSendingChange,
      resetNonce,
    },
    ref
  ) => {
    const intl = useIntl();
    const styles = useStyles(styleFn);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const isSendPending = useRef(false);
    const hasInitializedDraft = useRef(false);
    const prevContractId = useRef<number | undefined>(undefined);
    const { fileProgressContext, setFileProgressContext } = useContext(
      FileProgressContext
    );

    const [draft, setDraft] = useState<OffersMessagesDraft>(() =>
      createInitialDraft(contract)
    );
    const [savedDraft, setSavedDraft] = useState<OffersMessagesDraft>(() =>
      createInitialDraft(contract)
    );

    // Confirmation modal state for attachment deletion
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const [attachmentToDelete, setAttachmentToDelete] = useState<{
      id: string;
      name: string;
      fileGuid?: string;
    } | null>(null);

    // Drawer state for file preview
    const [previewDrawerVisible, setPreviewDrawerVisible] = useState(false);
    const [previewFile, setPreviewFile] = useState<{
      url: string;
      name: string;
      extension: string;
    } | null>(null);
    const [isLoadingUrl, setIsLoadingUrl] = useState(false);

    const currentMsgQueryKey = [
      'eval_current_instant_message',
      projectId,
      tenderId,
      contractId,
    ];

    // Query current draft
    const { data: currentMsg } = useQuery(
      currentMsgQueryKey,
      () =>
        getEvaluationCurrentInstantMessage({ projectId, tenderId, contractId }),
      { enabled: !!visible && !!contractId && !readOnly }
    );

    // Fetch full message details when in read-only mode
    const readOnlyMessageId = readOnlyData?.id || readOnlyData?.message_id;

    const { data: fullReadOnlyMessage } = useQuery(
      [
        'eval_instant_message_readonly',
        projectId,
        tenderId,
        readOnlyMessageId,
        shareId,
      ],
      () => {
        if (!readOnlyMessageId) return null;

        if (shareId && shareKey) {
          // Vendor side
          return getGlobalEvaluationInstantMessageVendor({
            shareId,
            shareKey,
            instantMessageId: readOnlyMessageId,
          });
        } else {
          // Project side
          return getGlobalEvaluationInstantMessage({
            projectId,
            tenderId,
            instantMessageId: readOnlyMessageId,
          });
        }
      },
      {
        enabled: !!visible && !!readOnly && !!readOnlyMessageId,
      }
    );

    // Query recipients for canSend validation
    const { data: receivers } = useQuery(
      ['eval_instant_message_receivers', projectId, tenderId, contractId],
      () =>
        getEvaluationInstantMessageReceivers({
          projectId,
          tenderId,
          contractId,
        }),
      { enabled: !!visible && !!contractId && !readOnly }
    );

    // Initialize draft from API
    useEffect(() => {
      if (!currentMsg) {
        hasInitializedDraft.current = false;
        return;
      }

      // Only initialize draft on first load or when switching contracts
      if (
        !hasInitializedDraft.current ||
        contract?.id !== prevContractId.current
      ) {
        const attachments: LocalAttachment[] = (
          currentMsg.attachments ?? []
        ).map((a) => ({
          id: a.file_guid,
          name: a.name,
          size: 0,
          fileGuid: a.file_guid,
        }));

        setDraft((prev) => ({
          ...prev,
          title: currentMsg.title ?? '',
          message: currentMsg.message ?? '',
          attachments,
        }));

        setSavedDraft((prev) => ({
          ...prev,
          title: currentMsg.title ?? '',
          message: currentMsg.message ?? '',
          attachments: attachments.map((a) => ({ ...a })),
        }));

        hasInitializedDraft.current = true;
        prevContractId.current = contract?.id;
      } else {
        // Only update attachments if needed (after upload success)
        const newAttachments = (currentMsg.attachments ?? []).map((a) => ({
          id: a.file_guid,
          name: a.name,
          size: 0,
          fileGuid: a.file_guid,
        }));

        setDraft((prev) => ({ ...prev, attachments: newAttachments }));
        setSavedDraft((prev) => ({
          ...prev,
          attachments: newAttachments.map((a) => ({ ...a })),
        }));
      }
    }, [currentMsg?.id, currentMsg?.attachments?.length, contract?.id]);

    // Update draft recipients when receivers query returns
    useEffect(() => {
      if (!receivers?.length) {
        setDraft((prev) => ({ ...prev, recipients: [] }));
        return;
      }

      const mapped: MessageRecipient[] = receivers.map((r, i) => ({
        id: r.email || `receiver-${i}`,
        name: r.full_name,
        email: r.email,
        company: r.company_name,
        role: 'Offer recipient',
        isPrimary: i === 0,
      }));

      setDraft((prev) => ({ ...prev, recipients: mapped }));
      setSavedDraft((prev) => ({ ...prev, recipients: mapped }));
    }, [receivers]);

    // Watch for upload completion and refetch
    useEffect(() => {
      if (!contractId) return;

      const ourUploads = fileProgressContext.filter(
        (f: any) =>
          f.isEvaluationInstantMessageAttachment && f.contractId === contractId
      );

      const allDone =
        ourUploads.length > 0 && ourUploads.every((f) => f.isDone);
      const anySuccess = ourUploads.some((f) => f.isDone && !f.error);

      if (allDone && anySuccess) {
        queryCache.invalidateQueries(currentMsgQueryKey);
      }
    }, [fileProgressContext, contractId, currentMsgQueryKey]);

    // Reset when parent requests it (via resetNonce)
    useEffect(() => {
      if (resetNonce !== undefined && resetNonce > 0) {
        setDraft(cloneDraft(savedDraft));
      }
    }, [resetNonce, savedDraft]);

    const buildBody = () => ({
      title: draft.title,
      message: draft.message,
      reference: contract?.reference ?? '',
    });

    const [doUpdate, { isLoading: isSaving }] = useMutation(
      updateEvaluationCurrentInstantMessage,
      {
        onSuccess: () => {
          queryCache.invalidateQueries(currentMsgQueryKey);
          setSavedDraft(cloneDraft(draft));
          if (isSendPending.current) {
            isSendPending.current = false;
            doSend({ projectId, tenderId, contractId, body: buildBody() });
          } else {
            message.success(intl.formatMessage({ id: 'draft-saved-success' }));
          }
        },
      }
    );

    const [doSend, { isLoading: isSending }] = useMutation(
      sendEvaluationCurrentInstantMessage,
      {
        onSuccess: () => {
          // Reset draft to empty state after successful send
          const emptyDraft = createInitialDraft(contract);
          setDraft(emptyDraft);
          setSavedDraft(cloneDraft(emptyDraft));
          hasInitializedDraft.current = false;

          queryCache.invalidateQueries(currentMsgQueryKey);
          queryCache.invalidateQueries([
            'all_tender_messages',
            projectId,
            tenderId,
          ]);

          // Show success notification
          message.success({
            title: intl.formatMessage({ id: 'message-sent-success-title' }),
            content: intl.formatMessage({ id: 'message-sent-success-content' }),
          });

          // Notify parent of success
          onSendSuccess?.();
        },
        onError: (error: any) => {
          // Show error notification
          message.error({
            title: intl.formatMessage({ id: 'message-sent-error-title' }),
            content: intl.formatMessage({ id: 'message-sent-error-content' }),
          });
        },
      }
    );

    const [
      doDeleteAttachment,
      { isLoading: isDeletingAttachment },
    ] = useMutation(deleteEvaluationCurrentInstantMessageAttachment, {
      onSuccess: () => {
        message.success(
          intl.formatMessage({ id: 'attachment-delete-successful' })
        );
        queryCache.invalidateQueries(currentMsgQueryKey);
      },
      onError: () => {
        message.error(intl.formatMessage({ id: 'attachment-delete-failed' }));
      },
    });

    const hasChanges = useMemo(() => {
      const draftAttachmentSignature = draft.attachments
        .map((item) => `${item.id}_${item.name}_${item.size}_${item.fileGuid}`)
        .join('|');

      const savedAttachmentSignature = savedDraft.attachments
        .map((item) => `${item.id}_${item.name}_${item.size}_${item.fileGuid}`)
        .join('|');

      return (
        draft.title !== savedDraft.title ||
        draft.message !== savedDraft.message ||
        draftAttachmentSignature !== savedAttachmentSignature
      );
    }, [draft, savedDraft]);

    const canSend = useMemo(
      () =>
        !!draft.title.trim() &&
        !!draft.message.trim() &&
        draft.recipients.length > 0,
      [draft.title, draft.message, draft.recipients.length]
    );

    // Notify parent of dirty state changes
    useEffect(() => {
      onDirtyChange(hasChanges);
    }, [hasChanges, onDirtyChange]);

    // Notify parent of canSend changes
    useEffect(() => {
      onCanSendChange(canSend);
    }, [canSend, onCanSendChange]);

    // Notify parent of sending state changes
    useEffect(() => {
      onSendingChange(isSending || isSaving);
    }, [isSending, isSaving, onSendingChange]);

    const handleSave = () => {
      doUpdate({ projectId, tenderId, contractId, body: buildBody() });
    };

    const handleReset = () => {
      setDraft(cloneDraft(savedDraft));
    };

    const openAttachmentPicker = () => {
      fileInputRef.current?.click();
    };

    const handleAttachmentSelected = (
      e: React.ChangeEvent<HTMLInputElement>
    ) => {
      const picked = Array.from(e.target.files ?? []);
      if (!picked.length) return;

      e.target.value = '';

      const remainingSlots = Math.max(
        0,
        MAX_ATTACHMENTS - draft.attachments.length
      );

      if (remainingSlots <= 0) {
        message.error(`Maximum ${MAX_ATTACHMENTS} files.`);
        return;
      }

      const filesToAdd = picked.slice(0, remainingSlots);
      const oversize = filesToAdd.find(
        (file) => file.size > MAX_ATTACHMENT_SIZE_BYTES
      );

      if (oversize) {
        message.error(intl.formatMessage({ id: 'upload-max-4mb' }));
        return;
      }

      // Push files to FileProgressContext for TUS upload
      setFileProgressContext((prev) => [
        ...prev,
        ...filesToAdd.map(
          (file) =>
            ({
              id: uuid(),
              file,
              projectId,
              tenderId: Number(tenderId),
              contractId,
              versionStr: ' ',
              description: ' ',
              isDone: false,
              shouldStartUpload: true,
              entityCreated: false,
              progress: {
                bytesTotal: file.size,
                bytesUploaded: 0,
                percentage: '0',
              },
              isEvaluationInstantMessageAttachment: true,
            } as any)
        ),
      ]);
    };

    const handleReadOnlyDownloadAttachment = async (fileGuid: string) => {
      const instantMessageId = (readOnlyData?.message_id ||
        readOnlyData?.id) as number;
      let url = '';

      if (shareId && shareKey) {
        url = await getGlobalEvaluationInstantMessageAttachmentLocationVendor({
          shareId,
          shareKey,
          instantMessageId,
          fileGuid,
        });
      } else {
        url = await getGlobalEvaluationInstantMessageAttachmentLocation({
          projectId,
          tenderId,
          instantMessageId,
          fileGuid,
        });
      }

      if (url) window.open(url, '_blank');
    };

    const handleDownloadAttachment = async (fileGuid: string) => {
      const url = await getEvaluationCurrentInstantMessageAttachmentLocation({
        projectId,
        tenderId,
        contractId,
        fileGuid,
      });
      if (url) window.open(url, '_blank');
    };

    // Helper functions for drawer preview
    const isPreviewable = (filename: string): boolean => {
      const ext = filename.split('.').pop()?.toLowerCase() || '';
      const isPdf = /^pdf$/i.test(ext);
      const isImage = /^(jpg|jpeg|png|gif|webp)$/i.test(ext);
      return isPdf || isImage;
    };

    const handleDrawerClose = () => {
      setPreviewDrawerVisible(false);
      setPreviewFile(null);
    };

    const handleFileDownload = () => {
      if (previewFile?.url) {
        const link = document.createElement('a');
        link.href = previewFile.url;
        link.download = previewFile.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    };

    const handleAttachmentPreview = async (
      fileGuid: string,
      filename: string
    ) => {
      const extension = filename.split('.').pop() || '';
      if (!isPreviewable(filename)) return;

      // Open drawer immediately with empty URL (shows loading spinner)
      setPreviewFile({ url: '', name: filename, extension });
      setPreviewDrawerVisible(true);
      setIsLoadingUrl(true);

      try {
        let url = '';

        if (readOnly) {
          // Read-only mode
          const instantMessageId = (readOnlyData?.message_id ||
            readOnlyData?.id) as number;

          if (shareId && shareKey) {
            // Vendor side
            url = await getGlobalEvaluationInstantMessageAttachmentLocationVendor(
              {
                shareId,
                shareKey,
                instantMessageId,
                fileGuid,
              }
            );
          } else {
            // Project side
            url = await getGlobalEvaluationInstantMessageAttachmentLocation({
              projectId,
              tenderId,
              instantMessageId,
              fileGuid,
            });
          }
        } else {
          // Editable mode
          url = await getEvaluationCurrentInstantMessageAttachmentLocation({
            projectId,
            tenderId,
            contractId,
            fileGuid,
          });
        }

        if (!url) {
          message.error(
            intl.formatMessage({ id: 'attachment-download-failed' })
          );
          setPreviewDrawerVisible(false);
          setPreviewFile(null);
          return;
        }

        // Update with actual URL - Viewer will re-render with content
        setPreviewFile({ url, name: filename, extension });
      } catch (e) {
        console.error(e);
        message.error(intl.formatMessage({ id: 'attachment-download-failed' }));
        setPreviewDrawerVisible(false);
        setPreviewFile(null);
      } finally {
        setIsLoadingUrl(false);
      }
    };

    const handleConfirmDeleteAttachment = async () => {
      if (!attachmentToDelete) return;

      if (attachmentToDelete.fileGuid) {
        await doDeleteAttachment({
          projectId,
          tenderId,
          contractId,
          fileGuid: attachmentToDelete.fileGuid,
        });
      }

      // Remove from local state after successful deletion
      setDraft((prev) => ({
        ...prev,
        attachments: prev.attachments.filter(
          (item) => item.id !== attachmentToDelete.id
        ),
      }));

      setAttachmentToDelete(null);
      setShowConfirmDeleteModal(false);
    };

    // Expose send method to parent via ref
    useImperativeHandle(ref, () => ({
      sendMessage: async () => {
        if (hasChanges) {
          isSendPending.current = true;
          doUpdate({ projectId, tenderId, contractId, body: buildBody() });
        } else {
          doSend({ projectId, tenderId, contractId, body: buildBody() });
        }
      },
    }));

    return (
      <div
        css={styles.wrapper}
        data-project-id={projectId}
        data-tender-id={tenderId}>
        {readOnly ? (
          /* ── READ-ONLY VIEW ── */
          <div css={styles.contentGrid}>
            <div css={styles.leftColumn}>
              <div css={styles.card}>
                <div css={styles.cardHeader}>
                  <div>
                    <Typography.Title level={5} css={styles.cardTitle}>
                      {intl.formatMessage({
                        id: 'offers-message-compose-title',
                      })}
                    </Typography.Title>
                  </div>
                </div>

                <div
                  css={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    gap: 8,
                  }}>
                  <div css={styles.formRowColumn}>
                    <div css={styles.label}>
                      {intl.formatMessage({ id: 'title' })}
                    </div>
                    <Typography.Text>
                      {fullReadOnlyMessage?.title || readOnlyData?.title || '-'}
                    </Typography.Text>
                  </div>

                  <div css={styles.formRowColumn}>
                    <div css={styles.label}>
                      {intl.formatMessage({ id: 'message' })}
                    </div>
                    <TextArea
                      value={
                        fullReadOnlyMessage?.message ||
                        readOnlyData?.description ||
                        '-'
                      }
                      readOnly
                      rows={7}
                      css={{
                        resize: 'none',
                        backgroundColor: '#ffffff',
                        cursor: 'text',
                        border: 'none',
                        padding: 0,
                        '&:focus, &:focus-within': {
                          outline: 'none !important',
                          boxShadow: 'none !important',
                          borderColor: 'transparent !important',
                        },
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div css={styles.rightColumn}>
              <div css={styles.card}>
                <div css={styles.cardHeader}>
                  <div>
                    <Typography.Title level={5} css={styles.cardTitle}>
                      {intl.formatMessage({
                        id: 'files',
                      })}
                    </Typography.Title>
                  </div>
                </div>

                <div
                  css={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                  }}>
                  {fullReadOnlyMessage?.attachments?.length ? (
                    <div css={styles.attachmentsList}>
                      {fullReadOnlyMessage.attachments.map(
                        (a: { file_guid: string; name: string }) => (
                          <div key={a.file_guid} css={styles.attachmentRow}>
                            <div css={styles.fileIcon}>
                              <PaperClipOutlined rev={''} />
                            </div>

                            <div css={styles.attachmentText}>
                              <Typography.Text
                                ellipsis
                                style={{
                                  display: 'block',
                                  width: '100%',
                                  userSelect: 'none',
                                  cursor: 'default',
                                }}
                                title={a.name}>
                                {a.name}
                              </Typography.Text>
                            </div>

                            <div css={styles.fileActions}>
                              {/* Preview icon for previewable files */}
                              {isPreviewable(a.name) && (
                                <IconButton
                                  icon={'eye'}
                                  iconStyle={'far'}
                                  color={'red'}
                                  customStyle={{ fontSize: '16px' }}
                                  click={() =>
                                    handleAttachmentPreview(a.file_guid, a.name)
                                  }
                                />
                              )}

                              <Button
                                type="default"
                                size="small"
                                icon={<DownloadOutlined rev={''} />}
                                onClick={() =>
                                  handleReadOnlyDownloadAttachment(a.file_guid)
                                }
                                css={{ minWidth: 32, height: 32 }}
                              />
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  ) : (
                    <div css={styles.emptyFilesBox}>
                      <Typography.Text type="secondary">
                        {intl.formatMessage({
                          id: 'no-attachment-available',
                        })}
                      </Typography.Text>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* ── EDITABLE VIEW ── */
          <div css={styles.contentGrid}>
            <div css={styles.leftColumn}>
              <div css={styles.card}>
                <div css={styles.cardHeader}>
                  <div>
                    <Typography.Title level={5} css={styles.cardTitle}>
                      {intl.formatMessage({
                        id: 'offers-message-compose-title',
                      })}
                    </Typography.Title>

                    <Typography.Text type="secondary" css={styles.cardHint}>
                      {intl.formatMessage({
                        id: 'offers-message-compose-hint',
                      })}
                    </Typography.Text>
                  </div>

                  <div css={styles.cardActions}>
                    <Button
                      size="small"
                      disabled={!hasChanges}
                      onClick={handleReset}>
                      {intl.formatMessage({ id: 'reset' })}
                    </Button>

                    <Button
                      size="small"
                      type="primary"
                      disabled={!hasChanges || isSaving}
                      loading={isSaving}
                      onClick={handleSave}>
                      {intl.formatMessage({
                        id: 'save-draft',
                      })}
                    </Button>
                  </div>
                </div>

                <div
                  css={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    gap: 16,
                  }}>
                  <div css={styles.formRowColumn}>
                    <div css={styles.label}>
                      {intl.formatMessage({ id: 'title' })}
                    </div>

                    <Input
                      value={draft.title}
                      placeholder={intl.formatMessage({
                        id: 'offers-message-title-placeholder',
                      })}
                      onChange={(e) =>
                        setDraft((prev) => ({
                          ...prev,
                          title: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div css={styles.formRowColumn}>
                    <div css={styles.label}>
                      {intl.formatMessage({ id: 'message' })}
                    </div>

                    <TextArea
                      value={draft.message}
                      placeholder={intl.formatMessage({
                        id: 'offers-message-body-placeholder',
                      })}
                      onChange={(e) =>
                        setDraft((prev) => ({
                          ...prev,
                          message: e.target.value,
                        }))
                      }
                      rows={7}
                      css={{ resize: 'none' }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div css={styles.rightColumn}>
              <div css={styles.card}>
                <div css={styles.cardHeader}>
                  <div>
                    <Typography.Title level={5} css={styles.cardTitle}>
                      {intl.formatMessage({
                        id: 'files',
                      })}
                    </Typography.Title>

                    <Typography.Text type="secondary" css={styles.cardHint}>
                      {intl.formatMessage({
                        id: 'offers-message-files-hint',
                      })}
                    </Typography.Text>
                  </div>

                  <Tag>
                    {draft.attachments.length}/{MAX_ATTACHMENTS}
                  </Tag>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleAttachmentSelected}
                />

                <div
                  css={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    gap: 14,
                  }}>
                  {draft.attachments.length < MAX_ATTACHMENTS && (
                    <button
                      type="button"
                      css={styles.uploadDropzone}
                      onClick={openAttachmentPicker}>
                      <div css={styles.uploadIcon}>
                        <PaperClipOutlined rev={''} />
                      </div>

                      <div css={styles.uploadTextBlock}>
                        <Typography.Text strong>
                          {intl.formatMessage({
                            id: 'upload',
                          })}
                        </Typography.Text>

                        <Typography.Text
                          type="secondary"
                          css={styles.uploadHint}>
                          {intl.formatMessage({ id: 'upload-max-4mb' })}, max{' '}
                          {MAX_ATTACHMENTS}
                        </Typography.Text>
                      </div>
                    </button>
                  )}

                  {draft.attachments.length ? (
                    <div css={styles.attachmentsList}>
                      {draft.attachments.map((attachment) => (
                        <div key={attachment.id} css={styles.attachmentRow}>
                          <div css={styles.fileIcon}>
                            <PaperClipOutlined rev={''} />
                          </div>

                          <div css={styles.attachmentText}>
                            <Typography.Text
                              ellipsis
                              style={{
                                display: 'block',
                                width: '100%',
                                userSelect: 'none',
                                cursor: 'default',
                              }}
                              title={attachment.name}>
                              {attachment.name}
                            </Typography.Text>
                          </div>

                          <div css={styles.fileActions}>
                            {/* Preview icon for previewable files */}
                            {isPreviewable(attachment.name) &&
                              attachment.fileGuid && (
                                <IconButton
                                  icon={'eye'}
                                  iconStyle={'far'}
                                  color={'red'}
                                  customStyle={{ fontSize: '16px' }}
                                  click={() =>
                                    handleAttachmentPreview(
                                      attachment.fileGuid!,
                                      attachment.name
                                    )
                                  }
                                />
                              )}

                            <Button
                              type="default"
                              size="small"
                              disabled={!attachment.fileGuid}
                              icon={<DownloadOutlined rev={''} />}
                              onClick={() =>
                                attachment.fileGuid &&
                                handleDownloadAttachment(attachment.fileGuid)
                              }
                              css={{ minWidth: 32, height: 32 }}
                            />

                            <Button
                              type="default"
                              size="small"
                              danger
                              icon={<DeleteOutlined rev={''} />}
                              onClick={() => {
                                setAttachmentToDelete({
                                  id: attachment.id,
                                  name: attachment.name,
                                  fileGuid: attachment.fileGuid,
                                });
                                setShowConfirmDeleteModal(true);
                              }}
                              css={{ minWidth: 32, height: 32 }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div css={styles.emptyFilesBox}>
                      <Typography.Text type="secondary">
                        {intl.formatMessage({
                          id: 'offers-message-no-files',
                        })}
                      </Typography.Text>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <SimpleConfirmModal
          visible={showConfirmDeleteModal}
          isLoading={isDeletingAttachment}
          onOk={handleConfirmDeleteAttachment}
          onCancel={() => {
            setAttachmentToDelete(null);
            setShowConfirmDeleteModal(false);
          }}
          modalTitle={`${intl.formatMessage({ id: 'delete-attachment' })} - ${
            attachmentToDelete?.name || ''
          }`}
          modalIcon={['far', 'trash']}
          okText={intl.formatMessage({ id: 'delete' })}
          okType="danger"
        />

        {/* Drawer preview */}
        {previewFile && (
          <Viewer
            mode="drawer"
            drawerVisible={previewDrawerVisible}
            onDrawerClose={handleDrawerClose}
            drawerTitle={previewFile.name}
            drawerWidth={800}
            fileUrl={previewFile.url}
            fileExtension={previewFile.extension}
            showDownloadButton={true}
            onDownload={handleFileDownload}
            isLoadingUrl={isLoadingUrl}
          />
        )}
      </div>
    );
  }
);

const styleFn: ThemedStyleFn = () => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    paddingBottom: 20,
    paddingTop: 2,
  },

  contentGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) 310px',
    gridTemplateRows: 'minmax(400px, auto)',
    gap: 16,
    alignItems: 'stretch',

    '@media (max-width: 900px)': {
      gridTemplateColumns: '1fr',
    },
  },

  leftColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    minWidth: 0,
  },

  rightColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    minWidth: 0,
  },

  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    padding: 16,
    border: '1px solid #f0f0f0',
    borderRadius: 10,
    background: '#ffffff',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.03)',
    minHeight: 0,
    height: '100%',
  },

  cardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
  },

  cardTitle: {
    margin: 0,
  },

  cardHint: {
    display: 'block',
    fontSize: 12,
    marginTop: 2,
  },

  cardActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },

  formRowColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },

  label: {
    fontWeight: 600,
  },

  uploadDropzone: {
    width: '100%',
    minHeight: 86,
    border: '1px dashed #d9d9d9',
    background: '#fafafa',
    borderRadius: 10,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'row',
    gap: 12,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 14,
    transition: 'all .2s ease',

    '&:hover:not(:disabled)': {
      borderColor: '#1890ff',
      background: '#f8fbff',
    },

    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.6,
    },
  },

  uploadIcon: {
    width: 34,
    height: 34,
    minWidth: 34,
    borderRadius: '50%',
    background: '#ffffff',
    color: '#1890ff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 1px 5px rgba(0, 0, 0, 0.06)',
  },

  uploadTextBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    textAlign: 'left',
  },

  uploadHint: {
    fontSize: 12,
  },

  attachmentsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    paddingRight: 4,
  },

  attachmentRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 12px',
    borderRadius: 8,
    background: '#fafafa',
    border: '1px solid #f5f5f5',
  },

  fileIcon: {
    width: 28,
    height: 28,
    minWidth: 28,
    borderRadius: 6,
    background: '#ffffff',
    color: '#8c8c8c',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  attachmentText: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
    flex: 1,
    overflow: 'hidden',
  },

  fileActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
  },

  emptyFilesBox: {
    padding: '12px 10px',
    borderRadius: 8,
    background: '#fafafa',
    textAlign: 'center',
  },
});
